<template>
    <div class="secretary-create-or-edit">
        <b-sidebar
            id="sidebar-task-handler"
            data-test="sidebar"
            sidebar-class="sidebar-lg"
            :visible="isSecretarySidebarActive"
            bg-variant="white"
            shadow
            backdrop
            no-header
            right
            @change="
                (val) => $emit('update:is-secretary-sidebar-active', val)
            "
            @hidden="clearFormData"
        >
            <template #default="{ hide }">
                <!-- Header -->
                <div
                    class="
                        d-flex
                        justify-content-between
                        align-items-center
                        content-sidebar-header
                        px-2
                        py-1
                        side-bar-header
                    "
                >
                    <h3 class="mb-0">
                        {{ sideBarTitle }}
                    </h3>

                    <div>
                        <feather-icon
                            class="ml-1 cursor-pointer"
                            icon="XIcon"
                            size="16"
                            @click="hide"
                        />
                    </div>
                </div>

                <!-- Body -->
                <validation-observer
                    v-slot="{ handleSubmit }"
                    ref="refFormObserver"
                >
                    <!-- Form -->
                    <b-form
                        class="p-2"
                        @submit.prevent="handleSubmit(onSubmit)"
                        @reset.prevent="resetForm"
                    >
                         <!-- Title -->
                         <validation-provider
                            v-slot="validationContext"
                            name="id"
                            rules="required"
                        >
                            <b-form-group label="ID" label-for="id">
                                <b-form-input
                                    id="id"
                                    v-model="secretaryLocal.nric"
                                    autofocus
                                    :state="
                                        getValidationState(validationContext)
                                    "
                                    :disabled = "isDisabled"
                                    trim
                                    placeholder="Enter director NRIC or password"
                                    data-test ="nric"
                                    @input="requestAutoFillFormData"
                                />

                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        
                        <validation-provider
                            v-slot="{ errors }"
                            name="ID type"
                            rules="required"
                        >
                            <b-form-group
                                label="ID type"
                                :state="errors.length > 0 ? false : null"
                            >
                                <v-select
                                    ref="idType"
                                    v-model="secretaryLocal.idType"
                                    class="custom-select-icon"
                                    :clearable="false"
                                    :disabled = "isDisabled"
                                    placeholder="Select ID type"
                                    label="name"
                                    :options="idType_options"
                                    @input="handleNationalityOnIdTypeChanged"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="nationality"
                            rules="required"
                        >
                            <b-form-group label="Nationality" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="nationality"
                                    ref="nationality"
                                    v-model="secretaryLocal.nationality"
                                    class="custom-select-icon"
                                    placeholder="Select nationality"
                                    label="text"
                                    :clearable="false"
                                    :disabled ="isDisabled || shouldDisabledNationality"
                                    :options="nationalityOptions"
                                >
                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="nationalityOptions.length <= 0" />
                                        <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-if="shouldShowExpiryDate"
                            v-slot="{ errors }"
                            name="date of expiry"
                            rules="required"
                        >
                            <b-form-group :state="errors.length > 0 ? false : null">
                                <slot name="label">
                                    <label id="dateOfExpiry" for="id">
                                        Date of expiry
                                        <vector
                                            data-type="icon"
                                            data-width="15px"
                                            data-height="15px"
                                            data-class="mr-.5"
                                            data-name="information"
                                        ></vector>
                                    </label>
                                </slot>

                                <b-popover
                                    target="dateOfExpiry"
                                    triggers="hover"
                                    placement="right"
                                    variant="black"
                                >
                                    Skip this if you are a Singapore citizen or permanent
                                    resident.
                                    <br />
                                    Fill in the date of expiry of your passport or FIN if
                                    you are a foreigner.
                                </b-popover>

                                <flat-pickr
                                    v-model="secretaryLocal.dateOfExpiry"
                                    placeholder="Enter date of expiry"
                                    class="form-control"
                                    :config="datePickerConfig"
                                    :disabled = "isDisabled"
                                    data-test="date-picker-dateOfExpiry"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="validationContext"
                            name="email address"
                            rules="required|email"
                        >
                            <b-form-group label="Email">
                                <b-form-input
                                    v-model="secretaryLocal.email"
                                    placeholder="Enter secretary email address"
                                    autofocus
                                    :state="
                                        getValidationState(validationContext)
                                    "
                                    :disabled ="isEmailDisabled"
                                    trim
                                    lazy
                                    data-test="secretary-email"
                                />

                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="validationContext"
                            name="name"
                            rules="required"
                        >
                            <b-form-group label="Name" label-for="name">
                                <b-form-input
                                    id="name"
                                    v-model="secretaryLocal.name"
                                    autofocus
                                    :state="
                                        getValidationState(validationContext)
                                    "
                                    :disabled = "isDisabled"
                                    trim
                                    placeholder="Enter secretary name"
                                    data-test ="secretary-name"
                                />

                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                                v-slot="{ errors }"
                                name="date of birth"
                                rules="required"
                            >
                                <b-form-group label="Date of birth" :state="errors.length > 0 ? false : null">
                                    <flat-pickr
                                        v-model="secretaryLocal.dob"
                                        placeholder="Enter secretary date of birth"
                                        class="form-control"
                                        data-test="secretary-dob"
                                        :config="datePickerConfig"
                                        :disabled = "isDisabled"
                                        :style="dynamicStyle"
                                        :state="errors.length > 0 ? false : null"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                        </validation-provider>

                        <validation-provider
                                v-slot="validationContext"
                                name="phone"
                                rules="required"
                            >
                                <b-form-group label="Phone">
                                    <b-form-input
                                        v-model="secretaryLocal.contactNumber"
                                        placeholder="Enter secretary contact number"
                                        autofocus
                                        :state="
                                            getValidationState(validationContext)
                                        "
                                        :disabled = "isDisabled"
                                        trim
                                        data-test="secretary-contact-number"
                                        type="tel"
                                    />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                        </validation-provider>

                        <validation-provider
                                v-slot="validationContext"
                                name="address"
                                rules="required"
                            >
                                <b-form-group label="Address">
                                    <b-form-textarea
                                        id="textarea"
                                        v-model="secretaryLocal.address"
                                        placeholder="Enter secretary address"

                                        autofocus
                                        :state="
                                            getValidationState(validationContext)
                                        "
                                        :disabled = "isDisabled"
                                        trim
                                        rows="3"
                                        max-rows="3"
                                        data-test="secretary-address"
                                    >
                                    </b-form-textarea>
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                        </validation-provider>

                        <validation-provider
                                v-slot="{ errors }"
                                name="appointment date"
                                rules="required"
                            >
                                <b-form-group label="Appointment date" :state="errors.length > 0 ? false : null">
                                    <flat-pickr
                                        v-model="secretaryLocal.appointmentDate"
                                        placeholder="Enter appointment date"
                                        class="form-control"
                                        :config="datePickerConfig"
                                        :style="dynamicStyle"
                                        :disabled = "isDisabled"
                                        data-test="appointment-date"
                                        :state="errors.length > 0 ? false : null"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                        </validation-provider>

                        <validation-provider
                                v-slot="{ errors }"
                                name="cessation date"
                            >
                                <b-form-group label="Cessation date" :state="errors.length > 0 ? false : null">
                                    <flat-pickr
                                        v-model="secretaryLocal.cessationDate"
                                        placeholder="Enter cessation date"
                                        class="form-control"
                                        :config="datePickerConfig"
                                        :style="dynamicStyle"
                                        :disabled = "isDisabled"
                                        data-test="cessation-date"
                                        :state="errors.length > 0 ? false : null"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                        </validation-provider>

                        <!-- Form Actions -->
                        <div class="d-flex">
                            <b-button
                                v-if="! isDisabled"
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                :disabled="dataSidebarActionLoading"
                                variant="primary"
                                class="
                                    mr-1
                                    mt-1
                                    d-flex
                                    justify-content-center
                                    align-items-center
                                "
                                type="submit"
                                data-test="create-secretary-btn"
                                >
                                <span :class="dataSidebarActionLoading ? 'mr-1' : ''">
                                    {{ dataStatus === 'edit' ? "Update" : "Add " }}
                                </span>

                                <img
                                    v-if="dataStatus === 'create' && !dataSidebarActionLoading"
                                    :src="'/illustrations/add-circle.svg'"
                                    class="ml-1"
                                    alt="add-logo"
                                />

                                <img
                                    v-if="dataStatus === 'edit' && !dataSidebarActionLoading"
                                    :src="'/illustrations/edit.svg'"
                                    class="ml-1"
                                    alt="edit-logo"
                                />
                                <normal-loader v-if="dataSidebarActionLoading" class="action-button left-10%" />
                            </b-button>

                            <b-button
                                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                class="
                                mt-1 
                                d-flex
                                justify-content-center
                                align-items-center"
                                variant="outline-primary"
                                data-test="cancel"
                                @click="$emit('update:is-secretary-sidebar-active', false)"
                                >
                                Cancel
                                <vector 
                                    data-type="icons" 
                                    data-width="15px" 
                                    data-height="15px" 
                                    data-name="cancel" 
                                    class="float-right ml-1"
                                    alt="cancel-logo"
                                />
                            </b-button>
                        </div>
                    </b-form>
                </validation-observer>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import {
    BSidebar,
    BForm,
    BFormInput,
    BPopover,
    BFormTextarea,
    BFormGroup,
    BButton,
    BFormInvalidFeedback,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import { debounce } from 'debounce';
import formValidation from "@core/comp-functions/forms/form-validation";
import { toRefs, ref, watch } from "@vue/composition-api";
import { NO_MATCHING_OPTIONS } from "@/constants/errors";
import IdType from "@/models/IdType";
import Nationality from "@/models/Nationality";
import { formatDate } from '@/helpers';
import { NRIC_CITIZEN_ID, SINGAPORE_CITIZEN } from "@/constants/config";

export default {
    components: {
        // BSV
        BForm,
        vSelect,
        BButton,
        BSidebar,
        flatPickr,
        BFormGroup,
        BFormInput,
        BPopover,
        BFormTextarea,
        BFormInvalidFeedback,

        // Form Validation
        ValidationProvider,
        ValidationObserver,
    },

    directives: {
        Ripple,
    },

    model: {
        prop: "isSecretarySidebarActive",
        event: "update:is-secretary-sidebar-active",
    },
    props: {
        isSecretarySidebarActive: {
            type: Boolean,
            required: true,
        },

        dataSidebarActionLoading: {
            type: Boolean,
            default: false,
        },

        dataSecretary: {
            type: Object,
            required: true,
        },
        
        dataStatus: { 
            type: String,
            default: "view"
        },

        dataAutoFillRequestedSecretary: {
            type: Object,
            default: null,
        }
    },

    data() {
        return {
            required,
            email,

            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },

            idType_options: [],
            nationalityOptions: [],
            NO_MATCHING_OPTIONS,

            shouldDisabledNationality: false,

            forceToCheckExpiry: false,
            forceRecheckExpiryDateCount: 1,
        };
    },

    computed: {
        isDisabled() {
            if(this.dataStatus === "view")
                return true;
            return false;
        },

        isEmailDisabled() {
            if(this.dataStatus === "view" || this.dataStatus !== 'create')
                return true;
            return false;
        },

        sideBarTitle() {
            if(this.dataStatus === "create")
                return "Add Secretary";

            if(this.dataStatus === "view")
                return "Secretary Details";

            return "Update Secretary";
        },

        dynamicStyle() {
            if(this.dataStatus === "view")
                return "background: #efefef; cursor: context-menu!important";
            return "background: white;";
        },

        shouldShowExpiryDate()
        {
            if(this.forceRecheckExpiryDateCount > 0 && this.secretaryLocal.idType?.name.includes("NRIC")) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.secretaryLocal.dateOfExpiry = null;
            }
            return ! this.secretaryLocal.idType?.name.includes("NRIC");
        },
    },

    watch: {
        dataAutoFillRequestedSecretary()
        {
            if(this.dataAutoFillRequestedSecretary)
            {
                this.resetSecretaryLocal();
                this.secretaryLocal.idType = {
                    id: this.dataAutoFillRequestedSecretary?.user_info?.id_type?.id,
                    name: this.dataAutoFillRequestedSecretary?.user_info?.id_type?.name
                };
                this.secretaryLocal.nationality = {
                    value: this.dataAutoFillRequestedSecretary?.user_info?.nationality?.id,
                    text: this.dataAutoFillRequestedSecretary?.user_info?.nationality?.name
                };
                this.secretaryLocal.dateOfExpiry = formatDate(this.dataAutoFillRequestedSecretary?.user_info?.date_of_expiry);
                this.secretaryLocal.nric = this.dataAutoFillRequestedSecretary?.user_info?.NRIC;
                this.secretaryLocal.userId = this.dataAutoFillRequestedSecretary.id;
                this.secretaryLocal.name = this.dataAutoFillRequestedSecretary?.user_info?.legal_name ?? this.dataAutoFillRequestedSecretary?.name;
                this.secretaryLocal.email = this.dataAutoFillRequestedSecretary?.email;
                this.secretaryLocal.dob = formatDate(this.dataAutoFillRequestedSecretary?.user_info?.DOB);
                this.secretaryLocal.email = this.dataAutoFillRequestedSecretary?.email;
                this.secretaryLocal.contactNumber = this.dataAutoFillRequestedSecretary?.user_info?.contact_number;
                this.secretaryLocal.address = this.dataAutoFillRequestedSecretary?.user_info?.address;

                this.forceRecheckExpiryDateCount += 1;
                this.forceToCheckExpiry = true;
            }
        },

        dataStatus(status) {
            if (status === 'edit' || status === 'create') {
                this.getSecretaryRequireData();
            }
        },

        'secretaryLocal.idType': {
            handler(data) {
                if(data && data.id) {
                    this.shouldDisabledNationality = data.id === NRIC_CITIZEN_ID;
                }
                else this.shouldDisabledNationality = false;
            }
        },
    },

    methods: {
        async getSecretaryRequireData() {
            // get id type options
            this.idType_options = await IdType.all();
            
            // get nationality
            const nationalities = await Nationality.all();
            this.nationalityOptions = nationalities.map(obj => ({
                text: obj.name,
                value: obj.id
            }));
        },

        // eslint-disable-next-line func-names
        requestAutoFillFormData: debounce(function(id) {
            this.$emit('request-autofill-secretary-data', id);
        }, 500),

        handleNationalityOnIdTypeChanged(data) {
            // intended when nric citizen or nric permanent user auto load change to passport or fin
            // this is just force to fire fire computed shouldShowExpiryDate property
            if(this.forceToCheckExpiry) {
                this.forceRecheckExpiryDateCount += 1;
            }
            
            this.shouldDisabledNationality = data.id === NRIC_CITIZEN_ID;

            if(data.id === NRIC_CITIZEN_ID) {
                const singaporeCitizen = this.nationalityOptions.filter(
                    nationality => nationality.text === SINGAPORE_CITIZEN
                ).map(
                    nation => ({
                        text: nation.text,
                        value: nation.value
                    })
                )[0];

                this.secretaryLocal.nationality = singaporeCitizen;
            }
        },
    },

    setup(props, { emit }) {
        // eslint-disable-next-line no-shadow
        function useSecretaryHandler(props, emit) {
            const secretaryLocal = ref(JSON.parse(JSON.stringify(props.dataSecretary.value)));

            const resetSecretaryLocal = () => {
                // eslint-disable-next-line no-use-before-define
                clearForm();
                secretaryLocal.value = JSON.parse(JSON.stringify(props.dataSecretary.value));
            };

            watch(props.dataSecretary, () => {
                resetSecretaryLocal();
            });
           
            const onSubmit = () => {
                const secretaryData = JSON.parse(JSON.stringify(secretaryLocal));
                
                if (props.dataSecretary.value.id) emit('update-secretary', secretaryData.value);
                else emit('add-secretary', secretaryData.value);
            };

            return {
                secretaryLocal, 

                // UI
                onSubmit,
                resetSecretaryLocal,
            };
        };
         
        const {
            secretaryLocal,

            // UI
            onSubmit, 
            resetSecretaryLocal
        } = useSecretaryHandler(toRefs(props), emit);

        const { refFormObserver, getValidationState, resetForm, clearForm } = formValidation(secretaryLocal);

        function clearFormData() {
            resetSecretaryLocal();
            clearForm();
        };

        return {
            // Add New
            onSubmit,
            secretaryLocal,

            // Form Validation
            resetForm,
            resetSecretaryLocal,
            clearForm,
            refFormObserver,
            getValidationState, 
            
            // Methods
            clearFormData,
        };
    },
};
</script>

<style scope>
.secretary-create-or-edit .preloader {
    width: 15px;
    height: 15px;
}

.secretary-create-or-edit .preloader div:before {
    background: var(--primary-color);
}

.secretary-create-or-edit .action-button .preloader div:before {
    background: white;
}

.side-bar-header{
   background: #f6f6f6 !important;
 } 
 
/* select disabled color overwrite */
.vs--disabled .vs__dropdown-toggle {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__search {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__open-indicator {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__selected{
    cursor: context-menu!important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>